import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Star, Shield, Sparkles, Landmark } from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/app/Logo";
import { InvoicesList } from "@/components/app/InvoicesList";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const PLANS = [
  {
    name: "Communauté",
    price: 0,
    tagline: "L'espace social santé — gratuit à vie",
    cta: "Créer mon compte",
    ctaLink: "/register",
    plan_key: null,
    color: "slate",
    features: [
      "Réseau social & feed spécialités",
      "Publier articles, cas cliniques, Q&A",
      "Groupes de spécialité",
      "Stories santé 24 h",
      "Messagerie confrères sécurisée",
      "Badge bleu vérifié RPPS (3 vérifications)",
      "Annuaire national des pros & établissements",
    ],
    audience: "Idéal pour tester la plateforme, se faire un réseau, exister en ligne.",
    highlighted: false,
  },
  {
    name: "Standard",
    price: 69,
    tagline: "Le cabinet augmenté",
    cta: "Démarrer 30 j gratuit",
    ctaLink: "/register?plan=standard",
    plan_key: "standard",
    color: "primary",
    features: [
      "Tout Communauté +",
      "Prise de rendez-vous en ligne",
      "Agenda intelligent + SMS J-1 & H-2",
      "Téléconsultation HDS (Whereby EU)",
      "Fiche cabinet SEO Google",
      "Messagerie patients illimitée",
      "DMP partagé (documents chiffrés)",
      "Feuilles de soins électroniques (via Icanopée)",
    ],
    audience: "Pour les praticiens en cabinet qui veulent digitaliser.",
    highlighted: true,
  },
  {
    name: "Premium",
    price: 129,
    tagline: "Le pack complet + IA",
    cta: "Démarrer 30 j gratuit",
    ctaLink: "/register?plan=premium",
    plan_key: "premium",
    color: "coral",
    features: [
      "Tout Standard +",
      "Ordonnance numérique VIDAL",
      "Alertes interactions médicamenteuses",
      "Tiers payant AMO + AMC (Icanopée)",
      "Statistiques cabinet avancées",
      "IA d'aide à la décision (Claude Sonnet)",
      "Formation continue certifiante",
      "Support prioritaire + Account Manager",
    ],
    audience: "Pour les cabinets qui veulent automatiser 100 % de leur workflow.",
    highlighted: false,
  },
];

export default function Pricing() {
  const { user } = useAuth();
  const [loadingKey, setLoadingKey] = useState(null);
  const [mine, setMine] = useState(null);
  const [canceling, setCanceling] = useState(false);

  useEffect(() => {
    if (!user) return;
    api.get("/subscriptions/me").then(({ data }) => setMine(data)).catch(() => {});
  }, [user]);

  const cancelSubscription = async () => {
    if (!window.confirm("Résilier votre abonnement ? Votre compte basculera sur le plan Communauté gratuit.")) return;
    setCanceling(true);
    try {
      await api.post("/subscriptions/cancel");
      toast.success("Abonnement résilié. Aucun prélèvement futur.");
      const { data } = await api.get("/subscriptions/me");
      setMine(data);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message);
    } finally {
      setCanceling(false);
    }
  };

  const startCheckout = async (plan) => {
    if (!plan.plan_key) return;
    // Not logged in → redirect to register with plan pre-selected
    if (!user) {
      window.location.href = `/register?plan=${plan.plan_key}&next=/tarifs?checkout=${plan.plan_key}`;
      return;
    }
    // Not a doctor account
    if (user.role !== "doctor" && !user.is_admin) {
      toast.error("Un compte praticien est requis pour souscrire un plan Doctobook.");
      return;
    }
    setLoadingKey(plan.plan_key);
    try {
      const { data } = await api.post("/subscriptions/checkout", {
        plan: plan.plan_key,
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error("Redirection GoCardless indisponible.");
      }
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || e.message || "Erreur GoCardless");
    } finally {
      setLoadingKey(null);
    }
  };
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <header className="border-b border-slate-100 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2"><Logo /></Link>
          <div className="flex gap-2">
            <Link to="/login"><Button variant="ghost" size="sm">Se connecter</Button></Link>
            <Link to="/register"><Button size="sm" className="btn-primary">Créer un compte</Button></Link>
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-6 py-16 text-center">
        <div className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-700 rounded-full px-3 py-1 text-xs font-bold uppercase tracking-wide mb-4">
          <Sparkles className="h-3 w-3" />30 jours d'essai gratuit · Aucun prélèvement avant J+30
        </div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-deep tracking-tight leading-[1.05]">
          Tarifs simples,<br />sans engagement
        </h1>
        <p className="mt-6 text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
          Choisissez le plan qui correspond à votre pratique. Modifiable ou résiliable à tout moment en un clic.
        </p>
        <div className="mt-6 inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-4 py-2 text-xs text-slate-600 font-semibold">
          <Shield className="h-3.5 w-3.5 text-emerald-600" />
          Doctobook ne perçoit aucune commission sur les honoraires médicaux — modèle SaaS 100 % conforme CNOM
        </div>
      </section>

      {mine?.subscription && mine.plan && (
        <section className="max-w-6xl mx-auto px-6 pb-10">
          <div className="bg-white border border-emerald-200 rounded-2xl p-6 flex flex-wrap items-center justify-between gap-4" data-testid="my-subscription-card">
            <div className="flex items-center gap-4">
              <div className="w-11 h-11 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center"><Landmark className="h-5 w-5" /></div>
              <div>
                <div className="font-display font-extrabold text-deep">Mon abonnement · {mine.plan === "premium" ? "Premium 129 €" : "Standard 69 €"} HT/mois</div>
                <div className="text-sm text-slate-600" data-testid="my-subscription-status">
                  {mine.subscription.status === "trialing" && <>Essai gratuit — 1er prélèvement SEPA le <b>{mine.subscription.first_debit_date && new Date(mine.subscription.first_debit_date).toLocaleDateString("fr-FR", { dateStyle: "long" })}</b></>}
                  {mine.subscription.status === "active" && "Abonnement actif — prélèvement SEPA mensuel"}
                  {mine.subscription.status === "payment_failed" && <span className="text-red-600 font-semibold">Dernier prélèvement refusé — vérifiez votre compte bancaire</span>}
                  {mine.subscription.status === "mandate_signed" && "Mandat signé — activation en cours"}
                  <span className="text-slate-400"> · mandat {mine.subscription.mandate_id}</span>
                  {mine.subscription.mock && <span className="ml-2 text-amber-700 font-semibold">(mode démonstration)</span>}
                </div>
              </div>
            </div>
            <Button variant="outline" onClick={cancelSubscription} disabled={canceling} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="cancel-subscription-btn">
              {canceling ? "Résiliation…" : "Résilier"}
            </Button>
          </div>
          <div className="mt-4"><InvoicesList /></div>
        </section>
      )}
      {user && !mine?.subscription && mine?.saas_status === "canceled" && (
        <section className="max-w-6xl mx-auto px-6 pb-10"><InvoicesList /></section>
      )}

      <section className="max-w-6xl mx-auto px-6 pb-24 grid gap-6 md:grid-cols-3">
        {PLANS.map((p) => (
          <div
            key={p.name}
            className={`rounded-2xl p-8 shadow-sm relative transition ${
              p.highlighted
                ? "bg-gradient-to-br from-[#618DB8] to-[#2A4365] text-white ring-4 ring-[#618DB8]/20 md:-mt-4 md:mb-4 shadow-xl"
                : "bg-white border border-slate-200"
            }`}
            data-testid={`plan-${p.name.toLowerCase()}`}
          >
            {p.highlighted && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#FF7A5C] text-white text-[10px] font-extrabold uppercase tracking-wider px-3 py-1 rounded-full flex items-center gap-1">
                <Star className="h-2.5 w-2.5 fill-white" />Le plus choisi
              </div>
            )}
            <div className="text-xs uppercase font-bold tracking-[0.2em] mb-2 opacity-75">{p.tagline}</div>
            <h3 className={`font-display text-3xl font-extrabold ${p.highlighted ? "text-white" : "text-deep"}`}>
              {p.name}
            </h3>
            <div className="mt-4 mb-6">
              {p.price === 0 ? (
                <div className="text-4xl font-extrabold">Gratuit</div>
              ) : (
                <div className="flex items-baseline gap-1">
                  <span className="text-4xl font-extrabold">{p.price}€</span>
                  <span className={`text-sm ${p.highlighted ? "text-white/70" : "text-slate-500"}`}>/ mois HT</span>
                </div>
              )}
              {p.price > 0 && (
                <div className={`text-xs mt-1 ${p.highlighted ? "text-white/70" : "text-slate-400"}`}>
                  Prélèvement SEPA mensuel (GoCardless) · résiliable à tout moment
                </div>
              )}
            </div>
            <ul className="space-y-2.5 mb-8">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm">
                  <Check className={`h-4 w-4 flex-shrink-0 mt-0.5 ${p.highlighted ? "text-[#FF7A5C]" : "text-emerald-500"}`} />
                  <span className={p.highlighted ? "text-white/95" : "text-slate-700"}>{f}</span>
                </li>
              ))}
            </ul>
            {p.plan_key ? (
              <Button
                onClick={() => startCheckout(p)}
                disabled={loadingKey === p.plan_key || mine?.plan === p.plan_key}
                className={`w-full font-bold ${
                  p.highlighted ? "bg-white text-[#2A4365] hover:bg-white/90" :
                  p.color === "coral" ? "btn-coral" : "btn-primary"
                }`}
                data-testid={`cta-${p.name.toLowerCase()}`}
              >
                {loadingKey === p.plan_key ? "Redirection GoCardless…" : mine?.plan === p.plan_key ? "Plan actuel" : p.cta}
              </Button>
            ) : (
              <Link to={p.ctaLink}>
                <Button
                  className={`w-full font-bold ${
                    p.highlighted ? "bg-white text-[#2A4365] hover:bg-white/90" :
                    p.color === "coral" ? "btn-coral" : "btn-primary"
                  }`}
                  data-testid={`cta-${p.name.toLowerCase()}`}
                >
                  {p.cta}
                </Button>
              </Link>
            )}
            <p className={`text-xs mt-4 text-center ${p.highlighted ? "text-white/70" : "text-slate-400"}`}>{p.audience}</p>
          </div>
        ))}
      </section>

      <section className="max-w-4xl mx-auto px-6 pb-24 space-y-4">
        <h2 className="font-display text-3xl font-extrabold text-deep tracking-tight text-center mb-8">Questions fréquentes</h2>
        {[
          {
            q: "Doctobook prélève-t-il une commission sur les honoraires ?",
            a: "Non. Doctobook est une association loi 1901 à but non lucratif (SIRET 922 912 126 00013), éditeur d'un logiciel SaaS conforme au Code de déontologie médicale (art. R.4127-53 CSP). Vous êtes facturé uniquement au forfait mensuel de votre plan. Les honoraires vont directement du patient à vous, jamais à Doctobook. Les revenus des abonnements financent exclusivement le fonctionnement et le développement de la plateforme.",
          },
          {
            q: "Puis-je essayer sans engagement ?",
            a: "Oui, 30 jours d'essai gratuit sur Standard et Premium. Vous signez simplement un mandat de prélèvement SEPA (IBAN) via GoCardless : le premier prélèvement n'intervient qu'à J+30, et vous pouvez résilier avant sans rien payer.",
          },
          {
            q: "Comment fonctionne le paiement des téléconsultations ?",
            a: "Doctobook n'encaisse jamais d'honoraires. Deux options : (1) paiement au cabinet (CB, espèces, chèque, Carte Vitale) ou (2) pour la téléconsultation, le patient règle directement le praticien via le lien de paiement que celui-ci renseigne dans son profil (SumUp, PayPal, Lydia Pro, Stripe Payment Link…), par virement, ou via la feuille de soins électronique (Icanopée). L'argent va directement sur votre compte bancaire pro.",
          },
          {
            q: "Mes données patients sont-elles en sécurité ?",
            a: "Oui. Hébergement HDS certifié en France (OVHcloud Healthcare, Roubaix/Gravelines). Chiffrement de bout en bout de la messagerie. Audit logs immuables 10 ans. Conformité RGPD & CNIL vérifiée.",
          },
          {
            q: "Puis-je annuler ?",
            a: "Oui, à tout moment. Aucun engagement, aucun frais de résiliation. Votre compte reste actif jusqu'à la fin de la période payée, puis bascule automatiquement sur le plan Communauté gratuit.",
          },
          {
            q: "Pourquoi le prélèvement SEPA plutôt que la carte bancaire ?",
            a: "Le mandat SEPA ne expire pas (contrairement à une carte), évite les échecs de renouvellement et coûte moins cher en frais (GoCardless ≈ 1 % + 0,20 € plafonné à 2 €), ce qui permet à l'association de garder des tarifs bas. Vous restez protégé par la garantie de remboursement SEPA de 8 semaines.",
          },
          {
            q: "TVA incluse ?",
            a: "Les prix affichés sont hors TVA. TVA 20 % en sus pour les entreprises françaises (récupérable si assujetti).",
          },
        ].map((item, i) => (
          <details key={i} className="bg-white border border-slate-200 rounded-xl p-4 group">
            <summary className="cursor-pointer font-bold text-deep flex items-center justify-between">
              {item.q}
              <span className="text-slate-400 group-open:rotate-45 transition text-2xl leading-none">+</span>
            </summary>
            <p className="mt-3 text-sm text-slate-600 leading-relaxed">{item.a}</p>
          </details>
        ))}
      </section>

      <footer className="border-t border-slate-100 bg-slate-50">
        <div className="max-w-6xl mx-auto px-6 py-8 text-xs text-slate-500 flex flex-wrap gap-4 justify-between">
          <div>© Doctobook · Tous droits réservés</div>
          <div className="flex gap-4">
            <Link to="/legal/mentions-legales" className="hover:text-slate-800">Mentions légales</Link>
            <Link to="/legal/cgu" className="hover:text-slate-800">CGU</Link>
            <Link to="/legal/privacy" className="hover:text-slate-800">Confidentialité</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
